import React, { useId, useState, useEffect, useRef } from 'react';
import { FACET_ROW_META, facetFilterEntryLabel, dedupeFacetIncludeExclude } from './tagConfigUtils';

function multiSummary(selected, useNegationLabels) {
  if (!selected.length) return 'Any';
  if (selected.length === 1) {
    return useNegationLabels ? facetFilterEntryLabel(selected[0]) : selected[0];
  }
  return `${selected.length} selected`;
}

function singleTriggerLabel(sel) {
  if (!sel.length) return 'Any';
  return facetFilterEntryLabel(sel[0]);
}

function toggleInList(list, orderedOptions, value, checked) {
  const set = new Set(list);
  if (checked) set.add(value);
  else set.delete(value);
  return orderedOptions.filter((o) => set.has(o));
}

/**
 * Facet controls: with `multiMode="dropdown"`, Camera uses the same button + panel UI as other
 * facets (radio list) so Safari matches flex-sized custom controls; native `<select>` only when
 * `multiMode="listbox"` for single-select.
 */
export default function FacetDropdowns({
  options,
  values,
  onValuesChange,
  disabled = false,
  hint = null,
  /** `"listbox"` = tall native multi-select; `"dropdown"` = closed button + checkbox panel. */
  multiMode = 'listbox',
  /** `"stacked"` (default) or `"toolbar"` for a horizontal wrap row (narrow gallery bar). */
  layout = 'stacked',
  hideHelp = false,
  /** When true (browse filters only), values may use `!Name` to exclude; UI offers exclude controls. */
  allowFacetNegation = false,
}) {
  const baseId = useId();
  const rootRef = useRef(null);
  const [openKey, setOpenKey] = useState(null);

  const setKey = (key, selectedList) => {
    onValuesChange({ ...values, [key]: selectedList });
  };

  const onMultiSelectChange = (key, baseList, e) => {
    let selected = Array.from(e.target.selectedOptions).map((o) => o.value);
    if (allowFacetNegation) selected = dedupeFacetIncludeExclude(selected, baseList);
    setKey(key, selected);
  };

  const onSingleSelectChange = (key, e) => {
    const v = e.target.value;
    setKey(key, v ? [v] : []);
  };

  useEffect(() => {
    if (openKey == null) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current || rootRef.current.contains(e.target)) return;
      setOpenKey(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenKey(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [openKey]);

  const wrapClass =
    layout === 'toolbar'
      ? 'facet-dropdowns facet-dropdowns--toolbar'
      : 'facet-dropdowns';

  return (
    <div ref={rootRef} className={wrapClass} role="group">
      {hint && <p className="facet-dropdowns-hint">{hint}</p>}
      {FACET_ROW_META.map(({ key, label, mode }) => {
        const list = Array.isArray(options?.[key]) ? options[key] : [];
        const sel = Array.isArray(values?.[key]) ? values[key] : [];
        const selectId = `${baseId}-${key}`;
        const isSingle = mode === 'single';
        const selectValue = isSingle ? (sel[0] || '') : undefined;
        const isOpen = openKey === key;
        const useDropdown = multiMode === 'dropdown';
        const useSingleDropdown = isSingle && useDropdown;
        const useMultiDropdown = !isSingle && useDropdown;
        const radioName = `${baseId}-${key}-single`;

        return (
          <div
            key={key}
            className={`facet-dropdowns-row${
              useMultiDropdown || useSingleDropdown ? ' facet-dropdowns-row--dd' : ''
            }`}
          >
            <label className="facet-dropdowns-label" htmlFor={list.length ? selectId : undefined}>
              {label}
            </label>
            {list.length === 0 ? (
              <p className="facet-dropdowns-empty">No options in config for this category.</p>
            ) : useSingleDropdown ? (
              <div className="facet-dropdowns-dd">
                <button
                  type="button"
                  id={selectId}
                  className="facet-dropdowns-dd-trigger"
                  disabled={disabled}
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                  onClick={() => setOpenKey(isOpen ? null : key)}
                >
                  <span className="facet-dropdowns-dd-trigger-text">
                    {allowFacetNegation ? singleTriggerLabel(sel) : sel.length ? sel[0] : 'Any'}
                  </span>
                  <span className="facet-dropdowns-dd-chevron" aria-hidden>
                    ▾
                  </span>
                </button>
                {isOpen && (
                  <div className="facet-dropdowns-dd-panel" role="radiogroup" aria-label={label}>
                    <label className="facet-dropdowns-dd-option">
                      <input
                        type="radio"
                        name={radioName}
                        checked={sel.length === 0}
                        disabled={disabled}
                        onChange={() => setKey(key, [])}
                      />
                      <span>Any</span>
                    </label>
                    {list.map((opt) => (
                      <label key={opt} className="facet-dropdowns-dd-option">
                        <input
                          type="radio"
                          name={radioName}
                          checked={sel[0] === opt}
                          disabled={disabled}
                          onChange={() => setKey(key, [opt])}
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                    {allowFacetNegation &&
                      list.map((opt) => (
                        <label
                          key={`!${opt}`}
                          className="facet-dropdowns-dd-option facet-dropdowns-dd-option--exclude"
                        >
                          <input
                            type="radio"
                            name={radioName}
                            checked={sel[0] === `!${opt}`}
                            disabled={disabled}
                            onChange={() => setKey(key, [`!${opt}`])}
                          />
                          <span>NOT {opt}</span>
                        </label>
                      ))}
                  </div>
                )}
              </div>
            ) : isSingle ? (
              <select
                id={selectId}
                className="facet-dropdowns-select facet-dropdowns-select--single"
                disabled={disabled}
                value={selectValue}
                onChange={(e) => onSingleSelectChange(key, e)}
              >
                <option value="">— None —</option>
                {list.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                {allowFacetNegation &&
                  list.map((opt) => (
                    <option key={`!${opt}`} value={`!${opt}`}>
                      NOT {opt}
                    </option>
                  ))}
              </select>
            ) : useMultiDropdown ? (
              <div className="facet-dropdowns-dd">
                <button
                  type="button"
                  id={selectId}
                  className="facet-dropdowns-dd-trigger"
                  disabled={disabled}
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                  onClick={() => setOpenKey(isOpen ? null : key)}
                >
                  <span className="facet-dropdowns-dd-trigger-text">
                    {multiSummary(sel, allowFacetNegation)}
                  </span>
                  <span className="facet-dropdowns-dd-chevron" aria-hidden>
                    ▾
                  </span>
                </button>
                {isOpen && (
                  <div
                    className="facet-dropdowns-dd-panel"
                    role="listbox"
                    aria-multiselectable="true"
                  >
                    {list.map((opt) => {
                      const inc = sel.includes(opt);
                      const exc = sel.includes(`!${opt}`);
                      if (allowFacetNegation) {
                        return (
                          <div key={opt} className="facet-dropdowns-dd-pair">
                            <label className="facet-dropdowns-dd-option facet-dropdowns-dd-option--grow">
                              <input
                                type="checkbox"
                                checked={inc}
                                disabled={disabled || exc}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  let next = sel.filter((s) => s !== opt && s !== `!${opt}`);
                                  if (on) next = [...next, opt];
                                  setKey(key, next);
                                }}
                              />
                              <span>{opt}</span>
                            </label>
                            <label className="facet-dropdowns-dd-option facet-dropdowns-dd-option--exclude-inline">
                              <input
                                type="checkbox"
                                checked={exc}
                                disabled={disabled || inc}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  let next = sel.filter((s) => s !== opt && s !== `!${opt}`);
                                  if (on) next = [...next, `!${opt}`];
                                  setKey(key, next);
                                }}
                              />
                              <span className="facet-dropdowns-exclude-label">NOT</span>
                            </label>
                          </div>
                        );
                      }
                      const checked = sel.includes(opt);
                      return (
                        <label key={opt} className="facet-dropdowns-dd-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => {
                              const next = toggleInList(sel, list, opt, e.target.checked);
                              setKey(key, next);
                            }}
                          />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <select
                id={selectId}
                className="facet-dropdowns-select"
                multiple
                size={Math.min(10, Math.max(4, list.length + (allowFacetNegation ? list.length : 0)))}
                disabled={disabled}
                value={sel}
                onChange={(e) => onMultiSelectChange(key, list, e)}
              >
                {list.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                {allowFacetNegation &&
                  list.map((opt) => (
                    <option key={`!${opt}`} value={`!${opt}`}>
                      NOT {opt}
                    </option>
                  ))}
              </select>
            )}
          </div>
        );
      })}
      {!hideHelp && hint ? <p className="facet-dropdowns-help">{hint}</p> : null}
    </div>
  );
}
